import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { EdgeToEdgeTabs } from '@/components/ui/EdgeToEdgeTabs/EdgeToEdgeTabs';
import {
  CreatePersonModal,
  type CreatePersonCreatedPayload,
  type CreatePersonRolePreset,
} from '@/components/CreatePersonModal/CreatePersonModal';
import { CatalogUnifiedSearch } from '@/components/CatalogManageModal/CatalogUnifiedSearch';
import { StudioCatalogForm } from '@/components/CatalogManageModal/StudioCatalogForm';
import { LegalEntityCatalogForm } from '@/components/CatalogManageModal/LegalEntityCatalogForm';
import {
  catalogTabForKind,
  type CatalogManageTabId,
  type CatalogSearchHit,
} from '@/components/CatalogManageModal/catalogSearchTypes';
import type { LegalEntity, Studio } from '@/api/client';
import '@/components/CreatePersonModal/CreatePersonModal.css';
import './CatalogManageModal.css';

const TAB_ITEMS = [
  { id: 'persons', label: 'Персоны' },
  { id: 'studios', label: 'Студии' },
  { id: 'legal_entities', label: 'Юрлица' },
] as const;

export interface CatalogManageModalProps {
  open: boolean;
  onClose: () => void;
  /** Стартовая вкладка при открытии (по умолчанию персоны) */
  initialTab?: CatalogManageTabId;
  rolePreset?: CreatePersonRolePreset | null;
  editionIdPreset?: string | null;
  personIdPreset?: string | null;
  studioIdPreset?: string | null;
  legalEntityIdPreset?: string | null;
  /** Префилл имени / названия при создании (не единый поиск) */
  initialNameSeed?: string | null;
  onPersonCreated?: (payload: CreatePersonCreatedPayload) => void;
  onStudioCreated?: (studio: Studio) => void;
  onLegalEntityCreated?: (entity: LegalEntity) => void;
}

export function CatalogManageModal({
  open,
  onClose,
  initialTab = 'persons',
  rolePreset = null,
  editionIdPreset = null,
  personIdPreset = null,
  studioIdPreset = null,
  legalEntityIdPreset = null,
  initialNameSeed = null,
  onPersonCreated,
  onStudioCreated,
  onLegalEntityCreated,
}: CatalogManageModalProps) {
  const [tab, setTab] = useState<CatalogManageTabId>(initialTab);
  const [personId, setPersonId] = useState<string | null>(null);
  const [personCreateNonce, setPersonCreateNonce] = useState(0);
  const [studioId, setStudioId] = useState<string | null>(null);
  const [studioCreateNonce, setStudioCreateNonce] = useState(0);
  const [legalEntityId, setLegalEntityId] = useState<string | null>(null);
  const [legalCreateNonce, setLegalCreateNonce] = useState(0);
  const [searchFlushKey, setSearchFlushKey] = useState(0);
  /** Префилл имени только для вкладки, с которой открыли создание */
  const [nameSeed, setNameSeed] = useState('');
  const [nameSeedTab, setNameSeedTab] = useState<CatalogManageTabId | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setPersonId(typeof personIdPreset === 'string' && personIdPreset.trim() ? personIdPreset.trim() : null);
    setPersonCreateNonce((n) => n + 1);
    setStudioId(typeof studioIdPreset === 'string' && studioIdPreset.trim() ? studioIdPreset.trim() : null);
    setStudioCreateNonce((n) => n + 1);
    setLegalEntityId(
      typeof legalEntityIdPreset === 'string' && legalEntityIdPreset.trim()
        ? legalEntityIdPreset.trim()
        : null
    );
    setLegalCreateNonce((n) => n + 1);
    const seed = typeof initialNameSeed === 'string' ? initialNameSeed.trim() : '';
    setNameSeed(seed);
    setNameSeedTab(seed ? initialTab : null);
    setSearchFlushKey((n) => n + 1);
  }, [open, initialTab, personIdPreset, studioIdPreset, legalEntityIdPreset, initialNameSeed]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSearchSelect = useCallback((hit: CatalogSearchHit) => {
    const nextTab = catalogTabForKind(hit.kind);
    setTab(nextTab);
    switch (hit.kind) {
      case 'person':
        setPersonId(hit.id);
        setPersonCreateNonce((n) => n + 1);
        break;
      case 'studio':
        setStudioId(hit.id);
        break;
      case 'legal_entity':
        setLegalEntityId(hit.id);
        break;
      default: {
        const _exhaustive: never = hit;
        return _exhaustive;
      }
    }
  }, []);

  const startCreateOnTab = useCallback((t: CatalogManageTabId) => {
    setTab(t);
    switch (t) {
      case 'persons':
        setPersonId(null);
        setPersonCreateNonce((n) => n + 1);
        break;
      case 'studios':
        setStudioId(null);
        setStudioCreateNonce((n) => n + 1);
        break;
      case 'legal_entities':
        setLegalEntityId(null);
        setLegalCreateNonce((n) => n + 1);
        break;
      default: {
        const _exhaustive: never = t;
        return _exhaustive;
      }
    }
  }, []);

  const editingExisting =
    Boolean(personIdPreset) || Boolean(studioIdPreset) || Boolean(legalEntityIdPreset);
  const personNameSeed =
    !editingExisting && !personId && nameSeedTab === 'persons' ? nameSeed : null;
  const studioNameSeed =
    !editingExisting && !studioId && nameSeedTab === 'studios' ? nameSeed : null;
  const legalEntityNameSeed =
    !editingExisting && !legalEntityId && nameSeedTab === 'legal_entities' ? nameSeed : null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Управление каталогом"
      size="md"
      headerVariant="island"
      className="create-person-modal catalog-manage-modal"
      overlayClassName="create-person-modal-overlay"
    >
      <div className="catalog-manage-modal__shell">
        <CatalogUnifiedSearch
          disabled={!open}
          debounceFlushKey={searchFlushKey}
          initialQuery=""
          onSelect={handleSearchSelect}
        />

        <div className="catalog-manage-modal__tabs">
          <EdgeToEdgeTabs
            items={[...TAB_ITEMS]}
            value={tab}
            onChange={(id) => setTab(id as CatalogManageTabId)}
            aria-label="Разделы каталога"
          />
        </div>

        <div className="catalog-manage-modal__panel" hidden={tab !== 'persons'}>
          <CreatePersonModal
            key={`person-${personId ?? 'new'}-${personCreateNonce}`}
            embedded
            open={open}
            onClose={handleClose}
            rolePreset={rolePreset}
            editionIdPreset={editionIdPreset}
            personIdPreset={personId}
            nameSeed={personNameSeed}
            onCreated={onPersonCreated}
            onRequestCreate={() => startCreateOnTab('persons')}
          />
        </div>

        <div className="catalog-manage-modal__panel" hidden={tab !== 'studios'}>
          <StudioCatalogForm
            studioId={studioId}
            createNonce={studioCreateNonce}
            nameSeed={studioNameSeed}
            onSaved={(s) => {
              const wasCreate = !studioId;
              setStudioId(s.id);
              if (wasCreate) onStudioCreated?.(s);
            }}
            onRequestCreate={() => startCreateOnTab('studios')}
          />
        </div>

        <div className="catalog-manage-modal__panel" hidden={tab !== 'legal_entities'}>
          <LegalEntityCatalogForm
            legalEntityId={legalEntityId}
            createNonce={legalCreateNonce}
            nameSeed={legalEntityNameSeed}
            onSaved={(le) => {
              const wasCreate = !legalEntityId;
              setLegalEntityId(le.id);
              if (wasCreate) onLegalEntityCreated?.(le);
            }}
            onRequestCreate={() => startCreateOnTab('legal_entities')}
          />
        </div>
      </div>
    </Modal>
  );
}
